import React from "react";
import { Paper, Box, Typography, Chip, Stack } from "@mui/material";
import {
  PersonOutlineRounded,
  CalendarTodayOutlined,
  SupportAgentOutlined,
  ChevronRightRounded,
} from "@mui/icons-material";
import { keyframes } from "@mui/system";
import {
  textPrimary,
  textSecondary,
  accentColor,
  successColor,
  successColor2,
  errorColor,
} from "../../../constants/consts";
import { useNavigate } from "react-router-dom";
import { parseServerDate } from "../../../utils/dateUtils"; // ← EKLENDİ

// --- Animasyonlar ---
const unreadPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 3px ${errorColor}22; transform: scale(1); }
  50%      { box-shadow: 0 0 0 6px ${errorColor}00; transform: scale(1.12); }
`;
const statusDotPulse = keyframes`
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.45; }
`;

const formatDate = (input) => {
  if (!input) return "";
  const d = parseServerDate(input);
  if (isNaN(d.getTime())) return input;
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(
    d.getHours(),
  )}:${p(d.getMinutes())}`;
};
function StatusPill({ status }) {
  const isCompleted = status?.toLowerCase().includes("tamam");
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.25,
        py: 0.5,
        borderRadius: 10,
        backgroundColor: isCompleted ? `${successColor}14` : `${accentColor}14`,
        color: isCompleted ? successColor2 : accentColor,
        fontSize: 11.5,
        fontWeight: 800,
        letterSpacing: "0.03em",
        textTransform: "uppercase",
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          backgroundColor: isCompleted ? successColor2 : accentColor,
          // Aktif talep noktası nazikçe nefes alsın
          animation: !isCompleted
            ? `${statusDotPulse} 1.8s ease-in-out infinite`
            : "none",
        }}
      />
      {status}
    </Box>
  );
}

function MetaItem({ icon: Icon, label, color = textSecondary }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Icon sx={{ fontSize: 14, color }} />
      <Typography
        variant="caption"
        sx={{ color, fontWeight: 600, fontSize: 12.5 }}
      >
        {label}
      </Typography>
    </Stack>
  );
}

export default function SupportCard({ support, onClick }) {
  const {
    title = "-",
    status = "",
    supporter = "",
    created_at = "",
    unread_user = 0,
    owner_username = "",
  } = support || {};

  const unread = parseInt(unread_user, 10) || 0;
  const isCompleted = status?.toLowerCase().includes("tamam");
  const hasUnread = unread > 0;
  const showSupporter = supporter && supporter !== "Havuz" && !isCompleted;

  const navigate = useNavigate();

  const handleOpen = () => {
    if (onClick) {
      onClick(support);
      return;
    }
    navigate(`/support/${support.id}/messages`, {
      state: {
        title: support.title,
        status: support.status,
      },
    });
  };

  return (
    <Paper
      elevation={0}
      onClick={handleOpen}
      sx={{
        p: { xs: 1.75, sm: 2 },
        borderRadius: 2.5,
        cursor: "pointer",
        backgroundColor: isCompleted ? "rgba(165,175,103,0.08)" : "#fff",
        boxShadow: "0 1px 4px rgba(15,50,65,0.05)",
        border: "1px solid",
        borderColor: hasUnread
          ? `${errorColor}55`
          : isCompleted
            ? "rgba(165,175,103,0.25)"
            : "rgba(15,50,65,0.07)",
        position: "relative",
        overflow: "hidden",
        // Yumuşak giriş + hover/active geri bildirim
        transition:
          "transform 0.25s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.25s ease, border-color 0.25s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 10px 24px rgba(15,50,65,0.12)",
          borderColor: hasUnread ? `${errorColor}80` : `${accentColor}55`,
          "& .MuiSvgIcon-root.card-chevron": {
            opacity: 0.9,
            transform: "translateX(3px)",
          },
        },
        "&:active": {
          transform: "translateY(0) scale(0.995)",
          boxShadow: "0 2px 8px rgba(15,50,65,0.08)",
        },
        // Sol kenar vurgusu
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 3.5,
          borderRadius: "2.5px 0 0 2.5px",
          backgroundColor: hasUnread
            ? errorColor
            : isCompleted
              ? successColor
              : "transparent",
          transition: "transform 0.3s ease",
          transformOrigin: "left center",
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1.5}
        sx={{ pl: hasUnread || isCompleted ? 0.75 : 0 }}
      >
        {/* Sol: Başlık + Meta */}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mb: 0.6 }}
          >
            {hasUnread && (
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  backgroundColor: errorColor,
                  flexShrink: 0,
                  animation: `${unreadPulse} 1.8s ease-in-out infinite`,
                }}
              />
            )}
            <Typography
              variant="subtitle1"
              fontWeight={700}
              color={textPrimary}
              sx={{
                lineHeight: 1.3,
                fontSize: 15,
                display: "-webkit-box",
                WebkitLineClamp: 1,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {title}
            </Typography>
          </Stack>

          <Stack
            direction="row"
            spacing={{ xs: 1.25, sm: 1.75 }}
            flexWrap="wrap"
            useFlexGap
            alignItems="center"
          >
            {owner_username && (
              <MetaItem icon={PersonOutlineRounded} label={owner_username} />
            )}
            <MetaItem
              icon={CalendarTodayOutlined}
              label={formatDate(created_at)}
            />
            {showSupporter && (
              <MetaItem
                icon={SupportAgentOutlined}
                label={supporter}
                color={accentColor}
              />
            )}
          </Stack>
        </Box>

        {/* Sağ: Unread chip + Status + Chevron */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ flexShrink: 0 }}
        >
          {hasUnread && (
            <Chip
              size="small"
              label={unread}
              sx={{
                height: 20,
                minWidth: 20,
                fontWeight: 800,
                fontSize: 11,
                backgroundColor: errorColor,
                color: "#fff",
                "& .MuiChip-label": { px: 0.6 },
                animation: `${unreadPulse} 1.8s ease-in-out infinite`,
              }}
            />
          )}
          <StatusPill status={status} />
          <ChevronRightRounded
            className="card-chevron"
            sx={{
              fontSize: 20,
              color: textSecondary,
              opacity: 0.45,
              transition: "opacity 0.2s ease, transform 0.25s ease",
            }}
          />
        </Stack>
      </Stack>
    </Paper>
  );
}
